import { useReveal } from "@/hooks/useReveal";

export default function PointOfView() {
  const { ref, visible } = useReveal();

  const stages = ["Signal", "Friction", "Cost", "Invoice"];

  return (
    <section
      id="point-of-view"
      ref={ref}
      data-testid="pov-section"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-radial-fade opacity-60 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="h-px w-12 bg-white/15" />
          <span className="num-label">SECTION 03 / POINT OF VIEW</span>
          <div className="h-px w-12 bg-white/15" />
        </div>

        <h2
          data-testid="pov-headline"
          className={`font-display font-bold tracking-tight text-axiom-text text-5xl sm:text-7xl lg:text-[110px] leading-[0.95] reveal ${
            visible ? "in" : ""
          }`}
        >
          The Invoice <br />
          <span className="text-axiom-accent">Is Too Late.</span>
        </h2>

        {/* Cost-signal stage visual */}
        <div
          className={`mt-14 max-w-3xl mx-auto reveal ${visible ? "in" : ""}`}
          style={{ transitionDelay: "120ms" }}
          data-testid="pov-stage-visual"
        >
          <div className="grid grid-cols-4 gap-2 sm:gap-4 items-center">
            {stages.map((s, i) => (
              <div key={s} className="relative">
                <div
                  className={`aspect-square border ${
                    i === stages.length - 1
                      ? "border-axiom-accent/60 bg-axiom-accent/5"
                      : "border-white/15 bg-axiom-surface/40"
                  } flex items-center justify-center`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <span
                      className={`signal-dot ${
                        i === stages.length - 1 ? "animate-pulse-signal" : "opacity-70"
                      }`}
                    />
                    <span className="num-label">STAGE.0{i + 1}</span>
                  </div>
                </div>
                <p className="mt-3 text-xs sm:text-sm text-axiom-muted font-medium tracking-wide">
                  {s}
                </p>
                {i < stages.length - 1 && (
                  <div className="hidden sm:block absolute top-1/2 -right-2 w-4 h-px bg-white/20" />
                )}
              </div>
            ))}
          </div>
          <p className="num-label mt-4">SIGNAL → FRICTION → COST → INVOICE</p>
        </div>

        <div className="mt-14 max-w-2xl mx-auto space-y-5 text-left sm:text-center">
          <p
            className={`text-axiom-muted text-base sm:text-lg leading-relaxed reveal ${
              visible ? "in" : ""
            }`}
            style={{ transitionDelay: "180ms" }}
          >
            By the time cost appears on a report, the operating decision has
            already happened. Axiom-AI works upstream, where signals become
            friction, friction becomes cost, and cost becomes executive
            exposure.
          </p>
          <p
            className={`text-axiom-text text-base sm:text-lg leading-relaxed reveal ${
              visible ? "in" : ""
            }`}
            style={{ transitionDelay: "260ms" }}
          >
            Axiom-AI does not treat symptoms as strategy. It identifies the system
            behavior creating the symptom and helps leaders redesign the
            decisions that produced it.
          </p>
        </div>
      </div>
    </section>
  );
}
